// utils/audio.ts
// Audio system for the React fretboard app with Web Audio API integration

// ================================================================================
// TYPES AND INTERFACES
// ================================================================================

export interface SoundTypes {
  readonly SINE: 'sine';
  readonly TRIANGLE: 'triangle';
  readonly SAWTOOTH: 'sawtooth';
  readonly SQUARE: 'square';
  readonly ACOUSTIC: 'acoustic';
}

export type SoundType = SoundTypes[keyof SoundTypes];

export interface SoundTypeLabels {
  readonly [key: string]: string;
}

// ================================================================================
// CONSTANTS AND CONFIGURATION
// ================================================================================

// Sound presets
export const SOUND_TYPES: SoundTypes = {
  SINE: 'sine',
  TRIANGLE: 'triangle',
  SAWTOOTH: 'sawtooth',
  SQUARE: 'square',
  ACOUSTIC: 'acoustic'  // Custom guitar-like sound
} as const;

// Sound type labels for UI
export const SOUND_TYPE_LABELS: SoundTypeLabels = {
  [SOUND_TYPES.SINE]: 'Sine Wave',
  [SOUND_TYPES.TRIANGLE]: 'Triangle Wave',
  [SOUND_TYPES.SAWTOOTH]: 'Sawtooth Wave',
  [SOUND_TYPES.SQUARE]: 'Square Wave',
  [SOUND_TYPES.ACOUSTIC]: 'Acoustic Guitar'
} as const;

// ================================================================================
// AUDIO CONTEXT MANAGEMENT
// ================================================================================

// Create a single AudioContext instance
let audioCtx: AudioContext | null = null;

// Initialize audio context on first use (due to browser autoplay policies)
const getAudioContext = (): AudioContext => {
  if (!audioCtx) {
    try {
      // Check if Web Audio API is supported
      if (typeof window === 'undefined' || (!window.AudioContext && !(window as any).webkitAudioContext)) {
        throw new Error('Web Audio API is not supported in this browser');
      }
      
      // TypeScript knows about AudioContext in browser environment
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to create AudioContext:', error);
      // Create a mock context that does nothing
      throw error;
    }
  }
  return audioCtx;
};

// Current sound setting
let currentSoundType: SoundType = SOUND_TYPES.ACOUSTIC;

// ================================================================================
// PUBLIC API FUNCTIONS
// ================================================================================

/**
 * Set the type of sound to use for notes
 * @param soundType - Type from SOUND_TYPES
 */
export function setSoundType(soundType: string): void {
  if (Object.values(SOUND_TYPES).includes(soundType as SoundType)) {
    currentSoundType = soundType as SoundType;
  } else {
    currentSoundType = SOUND_TYPES.ACOUSTIC;
  }
}

/**
 * Get the current sound type
 * @returns Current sound type
 */
export function getCurrentSoundType(): SoundType {
  return currentSoundType;
}

/**
 * Resume audio context if suspended (required by browser policies)
 */
export async function resumeAudioContext(): Promise<void> {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
}

/**
 * Play a note with the given frequency and duration.
 * @param frequency - Frequency of the note in Hz
 * @param duration - Duration of the note in milliseconds
 */
export async function playNote(frequency: number, duration: number = 500): Promise<void> {
  try {
    // Ensure audio context is active
    await resumeAudioContext();
    
    const ctx = getAudioContext();
    
    // Use acoustic simulation for guitar-like sounds
    if (currentSoundType === SOUND_TYPES.ACOUSTIC) {
      playGuitarLikeNote(frequency, duration);
      return;
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    // Set oscillator type based on current sound setting
    // OscillatorType only supports 'sine', 'triangle', 'sawtooth', 'square'
    oscillator.type = currentSoundType as 'sine' | 'triangle' | 'sawtooth' | 'square';
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
    
    // Connect oscillator -> gain -> destination
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Start and stop the oscillator with fade out
    oscillator.start();
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime); // Reduced volume for comfort
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    oscillator.stop(ctx.currentTime + duration / 1000 + 0.05);
  } catch (error) {
    // Silently handle audio playback errors - audio may not be supported
    // eslint-disable-next-line no-console
    console.debug('Audio playback error (this is normal if Web Audio API is not supported):', error);
  }
}

// ================================================================================
// GUITAR-LIKE SOUND SYNTHESIS
// ================================================================================

/**
 * Create a more guitar-like sound using multiple oscillators
 * @param frequency - Frequency of the note in Hz
 * @param duration - Duration of the note in milliseconds
 */
function playGuitarLikeNote(frequency: number, duration: number): void {
  const ctx = getAudioContext();
  const durationSec = duration / 1000;
  
  // Create main oscillator (fundamental frequency)
  const osc1 = ctx.createOscillator();
  osc1.type = 'triangle';
  osc1.frequency.value = frequency;
  
  // Create second oscillator (slight detune for richness)
  const osc2 = ctx.createOscillator();
  osc2.type = 'sawtooth';
  osc2.frequency.value = frequency * 1.005; // Slight detune
  osc2.detune.value = 4; // Additional minor detune
  
  // Create gain nodes for envelope
  const gainNode = ctx.createGain();
  const mainGain = ctx.createGain();
  
  // Create a biquad filter for guitar-like tone
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 2500;
  filter.Q.value = 1;
  
  // Connect nodes:
  // oscillators -> individual gains -> filter -> main gain -> destination
  osc1.connect(gainNode);
  osc2.connect(gainNode);
  gainNode.connect(filter);
  filter.connect(mainGain);
  mainGain.connect(ctx.destination);
  
  // Set initial gain and apply pluck envelope
  mainGain.gain.setValueAtTime(0.4, ctx.currentTime); // Reduced volume
  mainGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + durationSec);
  
  // Quick attack, fast initial decay
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(1, ctx.currentTime + 0.005);
  gainNode.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + 0.1);
  
  // Start and stop oscillators
  osc1.start();
  osc2.start();
  osc1.stop(ctx.currentTime + durationSec + 0.1);
  osc2.stop(ctx.currentTime + durationSec + 0.1);
  
  // Add string resonance effect
  addStringResonance(frequency, durationSec);
}

/**
 * Add string resonance harmonics for more authentic guitar sound
 * @param frequency - Base frequency
 * @param duration - Duration in seconds
 */
function addStringResonance(frequency: number, duration: number): void {
  const ctx = getAudioContext();
  
  // Create harmonics
  const harmonics: readonly number[] = [2, 3, 4, 5];
  const harmonicGain: readonly number[] = [0.1, 0.05, 0.025, 0.0125]; // Reduced gains for comfort
  
  for (const [i, harmonic] of harmonics.entries()) {
    const oscHarmonic = ctx.createOscillator();
    const gainHarmonic = ctx.createGain();
    
    oscHarmonic.type = 'sine';
    oscHarmonic.frequency.value = frequency * harmonic;
    
    gainHarmonic.gain.setValueAtTime(0, ctx.currentTime);
    gainHarmonic.gain.linearRampToValueAtTime(harmonicGain[i], ctx.currentTime + 0.01);
    gainHarmonic.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration * 0.75);
    
    oscHarmonic.connect(gainHarmonic);
    gainHarmonic.connect(ctx.destination);
    
    oscHarmonic.start();
    oscHarmonic.stop(ctx.currentTime + duration);
  }
}
